import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { protegerCambiosSinGuardar } from '../cambios-sin-guardar';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../../services/auth.service';
import { ActoresService } from '../../../services/actores.service';
import { Actor } from '../../../models/actor.model';
import { User } from '@angular/fire/auth';

@Component({
  standalone: true,
  selector: 'app-perfil-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTooltipModule,
  ],
  templateUrl: './perfil-dialog.component.html',
  styleUrls: ['./perfil-dialog.component.scss'],
})
export class PerfilDialogComponent implements OnInit {
  user: User | null = null;
  actor: Actor | null = null;

  form: FormGroup;
  private cerrarConAviso = protegerCambiosSinGuardar(() => this.form.dirty || !!this.avatarFile);
  loading = true;
  saving = false;

  avatarPreview: string | null = null;
  avatarFile: File | null = null;

  get consentimiento() { return this.actor?.comunicaciones?.comerciales; }
  get fechaConsentimiento() { return this.actor?.comunicaciones?.fechaConsentimiento; }

  constructor(
    public dialogRef: MatDialogRef<PerfilDialogComponent>,
    private fb: FormBuilder,
    private auth: AuthService,
    private actoresService: ActoresService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      Nom: [''],
      Cognom1: [''],
      Cognom2: [''],
      NifNie: [''],
      Telefono: [''],
      Ext: [''],
      Mobil: [''],
      Notas: [''],
      comunicaciones: [false],
    });
  }

  ngOnInit() {
    this.auth.currentUser.subscribe(user => {
      this.user = user;
      if (user?.email) {
        this.actoresService.getActorByEmail(user.email).subscribe({
          next: actor => {
            this.actor = actor;
            this.form.patchValue({
              Nom: actor.Nom ?? '',
              Cognom1: actor.Cognom1 ?? '',
              Cognom2: actor.Cognom2 ?? '',
              NifNie: actor.NifNie ?? '',
              Telefono: actor.Telefono ?? '',
              Ext: actor.Ext ?? '',
              Mobil: actor.Mobil ?? '',
              Notas: actor.Notas ?? '',
              comunicaciones: actor.comunicaciones?.comerciales ?? false,
            });
            // Si Firebase no tiene displayName pero el actor sí tiene nombre, sincronizar
            if (!user.displayName) {
              const displayName = [actor.Nom, actor.Cognom1, actor.Cognom2].filter(Boolean).join(' ');
              if (displayName) this.auth.updateUserProfile({ displayName }).catch(() => {});
            }
            this.loading = false;
          },
          error: () => { this.loading = false; }
        });
      }
    });
  }

  onAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.avatarFile = file;
    const reader = new FileReader();
    reader.onload = e => this.avatarPreview = e.target?.result as string;
    reader.readAsDataURL(file);
  }

  async save() {
    if (!this.actor) return;
    this.saving = true;

    try {
      // 1. Subir avatar si hay fichero pendiente
      if (this.avatarFile) {
        const { avatarUrl } = await this.actoresService.uploadAvatar(this.avatarFile).toPromise() as { avatarUrl: string };
        await this.auth.updateUserProfile({ photoURL: avatarUrl });
        this.avatarFile = null;
      }

      // 2. Datos personales + contacto + consentimiento en paralelo
      const { Nom, Cognom1, Cognom2, NifNie, Telefono, Ext, Mobil, Notas, comunicaciones: consentNuevo } = this.form.value;
      const consentCambiado = consentNuevo !== (this.actor.comunicaciones?.comerciales ?? false);

      await Promise.all([
        this.actoresService.updateMePerfil({ Nom, Cognom1, Cognom2, NifNie, Telefono, Ext, Mobil, Notas }).toPromise(),
        consentCambiado
          ? this.actoresService.updateComunicaciones(this.actor._id, consentNuevo).toPromise()
          : Promise.resolve(),
      ]);

      // 3. Sincronizar displayName en Firebase
      const displayName = [Nom, Cognom1, Cognom2].filter(Boolean).join(' ');
      if (displayName) await this.auth.updateUserProfile({ displayName });

      this.saving = false;
      this.snackBar.open('Perfil guardado', 'Cerrar', { duration: 3000 });
      this.dialogRef.close({ updated: true });

    } catch {
      this.saving = false;
      this.snackBar.open('Error al guardar el perfil', 'Cerrar', { duration: 3000 });
    }
  }

  close() { this.cerrarConAviso(); }
}
